import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as admin from '../../api/admin.ts';
import { formatBytes, formatDate, formatRelative } from '../../lib/format.ts';
import { toast } from '../../store/toast.ts';
import { Modal } from '../common/Modal.tsx';
import { EmptyState } from '../common/EmptyState.tsx';
import { IconPlus, IconSearch } from '../../lib/icons.tsx';
import { CreateUserModal } from './CreateUserModal.tsx';
import { UserActions } from './UserActions.tsx';
import { useAuth } from '../../store/auth.ts';

const GB = 1024 ** 3;

/** 표 열 폭. 머리글과 각 행이 같은 값을 써야 칼럼이 맞는다. */
const COLUMNS = '1fr 130px 180px 300px';
const QUOTAS = [10, 50, 100, 500, 1024, 2048];

export function UsersTab() {
  // 자기 계정에는 삭제 버튼을 막는다 (서버도 거부하지만 눌리지 않는 편이 낫다)
  const selfId = useAuth((st) => st.user?.id ?? '');
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const users = useQuery({
    queryKey: ['admin-users', search],
    queryFn: () => admin.listUsers(search || undefined),
  });

  const patch = useMutation({
    mutationFn: (v: { id: string; body: Parameters<typeof admin.updateUser>[1] }) =>
      admin.updateUser(v.id, v.body),
    onSuccess: () => {
      toast.success('변경했습니다');
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-user-detail'] });
    },
    onError: (e: Error) => { toast.error(e); },
  });

  const items = users.data?.items ?? [];

  return (
    <>
      <div className="row" style={{ marginBottom: 12 }}>
        <div className="topbar__search" style={{ maxWidth: 320 }}>
          <IconSearch size={16} />
          <input
            className="input" placeholder="이메일 또는 이름 검색"
            value={search} onChange={(e) => { setSearch(e.target.value); }}
          />
        </div>
        <div className="spacer" />
        <button className="btn btn--primary" onClick={() => { setCreating(true); }}>
          <IconPlus size={16} /> 사용자 추가
        </button>
      </div>

      {users.isLoading && <span className="spinner" />}
      {!users.isLoading && items.length === 0 && <EmptyState title="사용자가 없습니다" />}

      {items.map((user) => {
        const percent = Math.min(100, (user.used_bytes / Math.max(1, user.quota_bytes)) * 100);
        return (
          <div key={user.id} className="frow" style={{ gridTemplateColumns: COLUMNS }}>
            <div className="frow__name">
              <button
                className="frow__label"
                style={{ textAlign: 'left', font: 'inherit' }}
                onClick={() => { setDetailId(user.id); }}
              >
                {user.display_name}
              </button>
              <span className="faint" style={{ fontSize: 12 }}>{user.email}</span>
              {user.role === 'admin' && <span className="frow__badge">관리자</span>}
              {user.status !== 'active' && (
                <span className="frow__badge" style={{ color: 'var(--c-danger)' }}>비활성</span>
              )}
            </div>
            <div className="frow__meta">
              파일 {user.file_count.toLocaleString()}
              <div className="faint" style={{ fontSize: 11 }}>PC {user.device_count}대</div>
            </div>
            <div className="frow__meta">
              {formatBytes(user.used_bytes)} / {formatBytes(user.quota_bytes)}
              <div className="quota__bar" style={{ height: 4, marginTop: 4 }}>
                <div
                  className="quota__fill"
                  data-level={percent >= 95 ? 'full' : percent >= 80 ? 'warn' : 'ok'}
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>
            <div className="row">
              <select
                className="input" style={{ height: 30, width: 100 }}
                aria-label={`${user.display_name} 쿼터`}
                value={String(Math.round(user.quota_bytes / GB))}
                onChange={(e) => {
                  patch.mutate({ id: user.id, body: { quota_bytes: Number(e.target.value) * GB } });
                }}
              >
                {QUOTAS.map((gb) => <option key={gb} value={gb}>{gb} GB</option>)}
              </select>
              <button
                className="btn btn--sm"
                onClick={() => {
                  patch.mutate({
                    id: user.id,
                    body: { status: user.status === 'active' ? 'suspended' : 'active' },
                  });
                }}
              >
                {user.status === 'active' ? '비활성화' : '활성화'}
              </button>
              <UserActions
                user={user}
                selfId={selfId}
                onChanged={() => {
                  void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
                }}
              />
            </div>
          </div>
        );
      })}

      {creating && (
        <CreateUserModal
          onClose={() => { setCreating(false); }}
          onCreated={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
          }}
        />
      )}
      {detailId && <UserDetailModal id={detailId} onClose={() => { setDetailId(null); }} />}
    </>
  );
}

function UserDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-user-detail', id],
    queryFn: () => admin.userDetail(id),
  });

  return (
    <Modal title="사용자 상세" onClose={onClose} wide>
      {isLoading && <span className="spinner" />}
      {data && (
        <>
          <dl className="kv" style={{ marginBottom: 20 }}>
            <dt>이름</dt><dd>{data.display_name}</dd>
            <dt>이메일</dt><dd>{data.email}</dd>
            <dt>권한</dt><dd>{data.role === 'admin' ? '관리자' : '일반 사용자'}</dd>
            <dt>상태</dt><dd>{data.status === 'active' ? '정상' : '비활성'}</dd>
            <dt>가입일</dt><dd>{formatDate(data.created_at)}</dd>
            <dt>사용량</dt>
            <dd>{formatBytes(data.used_bytes)} / {formatBytes(data.quota_bytes)}</dd>
            <dt>휴지통</dt><dd>{formatBytes(data.trash_bytes)}</dd>
            <dt>활성 세션</dt><dd>{data.sessions}개</dd>
          </dl>

          <div className="sidebar__section" style={{ paddingLeft: 0 }}>드라이브</div>
          {data.namespaces.map((ns) => (
            <div key={ns.id} className="panel__row">
              <span className="spacer">
                {ns.name}
                <span className="faint" style={{ fontSize: 12 }}>
                  {' '}· {ns.type === 'personal' ? '개인' : '공유'} · {ns.role}
                </span>
              </span>
              <span className="muted">{ns.files.toLocaleString()}개 파일</span>
            </div>
          ))}

          <div className="sidebar__section" style={{ paddingLeft: 0 }}>연결된 PC</div>
          {data.devices.length === 0 && <p className="muted">없음</p>}
          {data.devices.map((device) => (
            <div key={device.id} className="panel__row">
              <span className="spacer">{device.name}
                <span className="faint" style={{ fontSize: 12 }}> · {device.platform}</span>
              </span>
              <span className="muted">
                {device.last_seen_at ? formatRelative(device.last_seen_at) : '접속 기록 없음'}
              </span>
            </div>
          ))}
        </>
      )}
    </Modal>
  );
}
